import { useCallback, useRef, useEffect, useState } from 'react'
import Safe, { SafeConfig, EthSafeTransaction } from '@safe-global/protocol-kit'
import { useAccount, usePublicClient } from 'wagmi'
import { getAddress } from 'viem'
import { useSafeWalletContext } from '../provider/SafeWalletContext'
import {
  getMinimalEIP1193Provider,
  createConnectionConfig,
} from '../utils/helpers'
import { SAFE_TX_GAS_DEFAULT } from '@/constants/main'
import { ensureSafeTransactionGas } from '@/utils/safeTransactionHelpers'
import { isUserRejectedError } from '@/utils/errorHandler'
import { getDemoPrivateKey, getDemoWalletAddress } from '@/utils/demoWallet'
import { getRpcUrlForChainId } from '@/config/env'

export default function useSafe(safeAddress: `0x${string}`) {
  const { address: signer, connector } = useAccount()
  const publicClient = usePublicClient()
  const { contractNetworks } = useSafeWalletContext()
  const demoPrivateKey = getDemoPrivateKey()
  const demoWalletAddress = getDemoWalletAddress()
  const kitRef = useRef<Safe | null>(null)
  const [isReady, setIsReady] = useState(false)
  const resolveConnection = useCallback(async () => {
    const rpcUrl = publicClient?.chain?.id
      ? getRpcUrlForChainId(publicClient.chain.id)
      : undefined
    const provider = demoPrivateKey
      ? rpcUrl
      : await getMinimalEIP1193Provider(connector)

    if (!provider) {
      return null
    }

    if (demoPrivateKey) {
      if (!demoWalletAddress) {
        return null
      }

      return {
        provider,
        signerForConfig: demoPrivateKey,
        signerAddress: demoWalletAddress,
      }
    }

    if (!signer) {
      return null
    }

    const checksummedSigner = getAddress(signer)
    return {
      provider,
      signerForConfig: checksummedSigner,
      signerAddress: checksummedSigner,
    }
  }, [publicClient, demoPrivateKey, connector, demoWalletAddress, signer])

  useEffect(() => {
    const connect = async () => {
      if (!publicClient || (!demoPrivateKey && (!signer || !connector))) {
        kitRef.current = null
        setIsReady(false)
        return
      }

      try {
        const connection = await resolveConnection()
        if (!connection) {
          kitRef.current = null
          setIsReady(false)
          return
        }

        const config: SafeConfig = createConnectionConfig(
          connection.provider,
          connection.signerForConfig,
          safeAddress,
          contractNetworks
        )

        const kit = await Safe.init(config)
        const connectedKit = await kit.connect(config)
        kitRef.current = connectedKit
        setIsReady(true)
      } catch (error) {
        console.error('Error connecting to Safe:', error)
        kitRef.current = null
        setIsReady(false)
      }
    }

    connect()
  }, [
    safeAddress,
    contractNetworks,
    publicClient,
    demoPrivateKey,
    signer,
    connector,
    resolveConnection,
  ])

  const buildSafeTransaction = useCallback(
    async (
      txs: Array<{
        to: string
        value: string
        data: string
        operation?: number
      }>,
      nonce?: number
    ): Promise<EthSafeTransaction | null> => {
      const kit = kitRef.current
      if (!kit) {
        console.error('Safe kit not initialized')
        return null
      }

      try {
        const normalizedTxs = txs.map((tx) => ({
          ...tx,
          value: tx.value || '0',
          data: tx.data || '0x',
          operation: tx.operation ?? 0,
        }))

        const options: {
          transactions: Array<{
            to: string
            value: string
            data: string
            operation?: number
          }>
          options?: {
            nonce?: number
            safeTxGas?: string
          }
        } = {
          transactions: normalizedTxs,
        }

        if (nonce !== undefined) {
          options.options = { nonce }
        }

        if (!options.options) {
          options.options = {}
        }

        options.options.safeTxGas = SAFE_TX_GAS_DEFAULT

        let safeTx
        try {
          safeTx = await kit.createTransaction(options)
        } catch (err) {
          console.warn('Gas estimation failed, using manual gas settings:', err)
          const errorMessage = err instanceof Error ? err.message : String(err)

          if (
            errorMessage.includes('gas') ||
            errorMessage.includes('estimation') ||
            errorMessage.includes('revert') ||
            errorMessage.includes('execution reverted')
          ) {
            try {
              const minimalOptions = {
                transactions: normalizedTxs,
                options: options.options,
              }
              safeTx = await kit.createTransaction(minimalOptions)
              ensureSafeTransactionGas(safeTx, SAFE_TX_GAS_DEFAULT)
            } catch (fallbackErr) {
              console.error(
                'Error building SafeTransaction with fallback:',
                fallbackErr
              )
              return null
            }
          } else {
            console.error('Error building SafeTransaction:', err)
            return null
          }
        }

        ensureSafeTransactionGas(safeTx, SAFE_TX_GAS_DEFAULT)
        return safeTx
      } catch (err) {
        console.error('Error building SafeTransaction:', err)
        return null
      }
    },
    []
  )

  const getSafeTransactionHash = useCallback(
    async (safeTx: EthSafeTransaction): Promise<string> => {
      const kit = kitRef.current
      if (!kit) return ''
      return kit.getTransactionHash(safeTx)
    },
    []
  )

  const getSafeNonce = useCallback(async (): Promise<number | null> => {
    const kit = kitRef.current
    if (!kit) return null
    try {
      const nonce = await kit.getNonce()
      return nonce
    } catch (error) {
      console.error('Error getting Safe nonce:', error)
      return null
    }
  }, [])

  const signSafeTransaction = useCallback(
    async (safeTx: EthSafeTransaction): Promise<EthSafeTransaction> => {
      const kit = kitRef.current
      if (!kit || (!signer && !demoPrivateKey)) {
        throw new Error('Safe kit not initialized or no signer')
      }

      try {
        const connection = await resolveConnection()
        if (!connection) {
          throw new Error('Provider not available')
        }

        const config: SafeConfig = createConnectionConfig(
          connection.provider,
          connection.signerForConfig,
          safeAddress,
          contractNetworks
        )

        const reconnectedKit = await Safe.init(config)
        const connectedKit = await reconnectedKit.connect(config)

        const signedTx = await connectedKit.signTransaction(safeTx)
        return signedTx
      } catch (err) {
        console.error('Error signing SafeTransaction:', err)
        throw err
      }
    },
    [signer, demoPrivateKey, resolveConnection, safeAddress, contractNetworks]
  )

  const isTransactionReadyToExecute = useCallback(
    async (safeTx: EthSafeTransaction): Promise<boolean> => {
      const kit = kitRef.current
      if (!kit) return false

      try {
        const threshold = await kit.getThreshold()

        const signatureCount = safeTx.signatures?.size || 0

        return signatureCount >= threshold
      } catch (err) {
        console.error('Error checking if transaction is ready to execute:', err)
        return false
      }
    },
    []
  )

  const getSafeThreshold = useCallback(async (): Promise<number | null> => {
    const kit = kitRef.current
    if (!kit) return null
    try {
      const threshold = await kit.getThreshold()
      return threshold
    } catch (error) {
      console.error('Error getting Safe threshold:', error)
      return null
    }
  }, [])

  const signRejection = useCallback(
    async (_safeTxHash: string, nonce: number): Promise<string> => {
      const kit = kitRef.current
      if (!kit || (!signer && !demoPrivateKey)) {
        throw new Error('Safe kit not initialized or no signer')
      }

      try {
        const connection = await resolveConnection()
        if (!connection) {
          throw new Error('Provider not available')
        }

        const config: SafeConfig = createConnectionConfig(
          connection.provider,
          connection.signerForConfig,
          safeAddress,
          contractNetworks
        )

        const reconnectedKit = await Safe.init(config)
        const connectedKit = await reconnectedKit.connect(config)

        const rejectionTx = await connectedKit.createRejectionTransaction(nonce)

        const signedRejection = await connectedKit.signTransaction(rejectionTx)

        const signatures = signedRejection.signatures
        if (signatures && signatures.size > 0) {
          const userSig = Array.from(signatures.values()).find(
            (sig) =>
              sig.signer.toLowerCase() === connection.signerAddress.toLowerCase()
          )
          if (userSig) {
            return userSig.data
          }
        }

        throw new Error(
          'Unable to extract signature from rejection transaction'
        )
      } catch (err) {
        console.error('Error signing rejection:', err)
        throw err
      }
    },
    [
      signer,
      safeAddress,
      contractNetworks,
      demoPrivateKey,
      resolveConnection,
    ]
  )

  const executeSafeTransaction = useCallback(
    async (safeTx: EthSafeTransaction): Promise<`0x${string}` | null> => {
      const kit = kitRef.current
      if (!kit) return null

      try {
        if (!safeTx.signatures || safeTx.signatures.size === 0) {
          ensureSafeTransactionGas(safeTx, SAFE_TX_GAS_DEFAULT)
        }

        const result = await kit.executeTransaction(safeTx)
        return result.hash as `0x${string}`
      } catch (err) {
        console.error('Error executing SafeTransaction:', err)

        if (isUserRejectedError(err)) {
          throw err
        }

        let errorMessage = 'Failed to execute transaction'
        if (err instanceof Error) {
          errorMessage = err.message
          if (err.message.includes('execution reverted')) {
            const revertMatch = err.message.match(
              /execution reverted(?: \(([^)]+)\))?/
            )
            if (revertMatch && revertMatch[1]) {
              errorMessage = `Transaction reverted: ${revertMatch[1]}`
            } else {
              errorMessage =
                'Transaction execution reverted. This may be due to insufficient gas, invalid parameters, or contract state.'
            }
          } else if (err.message.includes('gas')) {
            errorMessage =
              'Transaction failed due to gas estimation or execution. Please try again or contact support.'
          }
        } else if (typeof err === 'object' && err !== null) {
          if ('message' in err) {
            errorMessage = String(err.message)
          } else if ('reason' in err) {
            errorMessage = String(err.reason)
          }
        }

        const detailedError = new Error(errorMessage)
        if (err instanceof Error) {
          detailedError.stack = err.stack
        }

        throw detailedError
      }
    },
    []
  )

  return {
    isReady,
    buildSafeTransaction,
    getSafeTransactionHash,
    signSafeTransaction,
    executeSafeTransaction,
    getSafeNonce,
    isTransactionReadyToExecute,
    getSafeThreshold,
    signRejection,
  }
}
